/**
 * Marketing Report Widget
 * ========================
 * A single report widget displayed on the Marketing Tracker dashboard.
 * Renders different visualizations based on report type.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  BarChart3,
  LineChart,
  AreaChart,
  PieChart,
  Table2,
  Hash,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  RefreshCw,
  GripVertical,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart as RechartsLineChart,
  Line,
  AreaChart as RechartsAreaChart,
  Area,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// Chart colors palette
const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

// Format value based on metric format
const formatValue = (value, format = 'number', decimals = 0) => {
  if (value === null || value === undefined) return '-';
  
  const num = Number(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return `$${num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
    case 'percent':
      return `${num.toFixed(decimals)}%`;
    default:
      return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
};

const MarketingReportWidget = ({
  widget,
  report,
  onEdit,
  onDuplicate,
  onRemove,
  onRefresh,
  dateRangeFilter,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch report data
  const fetchData = useCallback(async () => {
    if (!report?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Build URL with optional date range filter override
      let url = `${API}/api/marketing/reports/${report.id}/run`;
      if (dateRangeFilter) {
        url += `?date_range_override=${dateRangeFilter}`;
      }
      const response = await axios.post(url);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.response?.data?.detail || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [report?.id, dateRangeFilter]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Get visualization icon
  const getVisualizationIcon = () => {
    switch (report?.visualization_type) {
      case 'line': return LineChart;
      case 'area': return AreaChart;
      case 'pie': return PieChart;
      case 'table': return Table2;
      case 'number': return Hash;
      default: return BarChart3;
    }
  };
  
  const VizIcon = getVisualizationIcon();
  
  // Transform data for charts
  const getChartData = () => {
    if (!data?.series?.length) return [];
    
    // For time-series, use the first series' points as base
    const firstSeries = data.series[0];
    if (!firstSeries?.points?.length) return [];
    
    return firstSeries.points.map((point, index) => {
      const row = { name: point.x };
      data.series.forEach(series => {
        const seriesPoint = series.points[index];
        row[series.metric_id] = seriesPoint?.y || 0;
      });
      return row;
    });
  };
  
  // Render chart based on visualization type
  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
          <AlertCircle className="h-6 w-6 mb-2 text-red-400" />
          <p className="text-xs text-center">{error}</p>
        </div>
      );
    }
    
    if (!data?.series?.length) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
          <VizIcon className="h-8 w-8 mb-2 opacity-30" />
          <p className="text-xs">No data available</p>
        </div>
      );
    }
    
    const chartData = getChartData();
    
    switch (report?.visualization_type) {
      case 'number':
        return renderNumberViz();
      case 'table':
        return renderTableViz();
      case 'pie':
        return renderPieChart(chartData);
      case 'line':
        return renderLineChart(chartData);
      case 'area':
        return renderAreaChart(chartData);
      default:
        return renderBarChart(chartData);
    }
  };
  
  // Number visualization - big KPI numbers
  const renderNumberViz = () => {
    const totals = data?.totals || {};
    
    return (
      <div className="flex flex-wrap gap-4 justify-center items-center h-full">
        {Object.entries(totals).map(([metricId, value], index) => {
          const series = data.series?.find(s => s.metric_id === metricId);
          return (
            <div key={metricId} className="text-center">
              <p className="text-3xl font-bold" style={{ color: CHART_COLORS[index % CHART_COLORS.length] }}>
                {formatValue(value)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {series?.metric_label || metricId}
              </p>
            </div>
          );
        })}
      </div>
    );
  };
  
  // Table visualization
  const renderTableViz = () => {
    const tableData = data?.table || {};
    const columns = tableData.columns || [];
    const rows = tableData.rows || [];
    
    if (!columns.length || !rows.length) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          <p className="text-xs">No table data</p>
        </div>
      );
    }
    
    return (
      <div className="overflow-auto h-full">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b">
              {columns.map((col, i) => (
                <th key={i} className="text-left p-2 font-medium">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-muted/30 hover:bg-muted/20">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="p-2">
                    {typeof cell === 'number' ? formatValue(cell) : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 20 && (
          <p className="text-[10px] text-muted-foreground text-center py-2">
            Showing 20 of {rows.length} rows
          </p>
        )}
      </div>
    );
  };
  
  // Bar chart
  const renderBarChart = (chartData) => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 10 }} 
          tickLine={false}
          axisLine={{ stroke: '#374151' }}
        />
        <YAxis 
          tick={{ fontSize: 10 }} 
          tickLine={false}
          axisLine={{ stroke: '#374151' }}
          tickFormatter={(v) => formatValue(v)}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'var(--background)', 
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '12px'
          }}
          formatter={(value) => formatValue(value)}
        />
        <Legend wrapperStyle={{ fontSize: '10px' }} />
        {data?.series?.map((series, index) => (
          <Bar
            key={series.metric_id}
            dataKey={series.metric_id}
            name={series.metric_label}
            fill={CHART_COLORS[index % CHART_COLORS.length]}
            radius={[4, 4, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
  
  // Line chart
  const renderLineChart = (chartData) => (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsLineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 10 }} 
          tickLine={false}
          axisLine={{ stroke: '#374151' }}
        />
        <YAxis 
          tick={{ fontSize: 10 }} 
          tickLine={false}
          axisLine={{ stroke: '#374151' }}
          tickFormatter={(v) => formatValue(v)}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'var(--background)', 
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '12px'
          }}
          formatter={(value) => formatValue(value)}
        />
        <Legend wrapperStyle={{ fontSize: '10px' }} />
        {data?.series?.map((series, index) => (
          <Line
            key={series.metric_id}
            type="monotone"
            dataKey={series.metric_id}
            name={series.metric_label}
            stroke={CHART_COLORS[index % CHART_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
  
  // Area chart
  const renderAreaChart = (chartData) => (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsAreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 10 }} 
          tickLine={false}
          axisLine={{ stroke: '#374151' }}
        />
        <YAxis 
          tick={{ fontSize: 10 }} 
          tickLine={false}
          axisLine={{ stroke: '#374151' }}
          tickFormatter={(v) => formatValue(v)}
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'var(--background)', 
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '12px'
          }}
          formatter={(value) => formatValue(value)}
        />
        <Legend wrapperStyle={{ fontSize: '10px' }} />
        {data?.series?.map((series, index) => (
          <Area
            key={series.metric_id}
            type="monotone"
            dataKey={series.metric_id}
            name={series.metric_label}
            stroke={CHART_COLORS[index % CHART_COLORS.length]}
            fill={CHART_COLORS[index % CHART_COLORS.length]}
            fillOpacity={0.2}
            strokeWidth={2}
          />
        ))}
      </RechartsAreaChart>
    </ResponsiveContainer>
  );
  
  // Pie chart
  const renderPieChart = (chartData) => {
    // For pie chart, we use totals instead of time series
    const totals = data?.totals || {};
    const pieData = Object.entries(totals).map(([metricId, value], index) => ({
      name: data.series?.find(s => s.metric_id === metricId)?.metric_label || metricId,
      value: value || 0,
      fill: CHART_COLORS[index % CHART_COLORS.length],
    }));
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={70}
            paddingAngle={2}
            dataKey="value"
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            labelLine={{ stroke: '#6b7280', strokeWidth: 1 }}
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'var(--background)', 
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '12px'
            }}
            formatter={(value) => formatValue(value)}
          />
          <Legend wrapperStyle={{ fontSize: '10px' }} />
        </RechartsPieChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card className="h-full flex flex-col overflow-hidden group">
      <CardHeader className="flex-shrink-0 pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Drag Handle */}
            <div className="widget-drag-handle cursor-grab opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            
            <CardTitle className="text-sm font-medium truncate flex-1">
              {report?.name || 'Untitled Report'}
            </CardTitle>
            
            <Badge variant="outline" className="text-[10px] h-5 flex-shrink-0">
              <VizIcon className="h-3 w-3 mr-1" />
              {report?.visualization_type}
            </Badge>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit?.(report)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate?.(report)}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => onRemove?.(widget.id)}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove from page
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Date range indicator */}
        {data?.meta?.date_range && (
          <p className="text-[10px] text-muted-foreground mt-1">
            {new Date(data.meta.date_range.start).toLocaleDateString()} - {new Date(data.meta.date_range.end).toLocaleDateString()}
          </p>
        )}
      </CardHeader>
      
      <CardContent className="flex-1 p-3 pt-0 min-h-0">
        {renderChart()}
      </CardContent>
    </Card>
  );
};

export default MarketingReportWidget;
